import { useEffect, useRef } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "@/lib/router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Layout } from "./components/Layout";
import { OnboardingWizard } from "./components/OnboardingWizard";
import { authApi } from "./api/auth";
import { healthApi } from "./api/health";
import { Dashboard } from "./pages/Dashboard";
import { Companies } from "./pages/Companies";
import { Agents } from "./pages/Agents";
import { AgentDetail } from "./pages/AgentDetail";
import { Projects } from "./pages/Projects";
import { ProjectDetail } from "./pages/ProjectDetail";
import { Issues } from "./pages/Issues";
import { IssueDetail } from "./pages/IssueDetail";
import { Goals } from "./pages/Goals";
import { GoalDetail } from "./pages/GoalDetail";
import { Approvals } from "./pages/Approvals";
import { ApprovalDetail } from "./pages/ApprovalDetail";
import { Costs } from "./pages/Costs";
import { Activity } from "./pages/Activity";
import { Inbox } from "./pages/Inbox";
import { SimpleInbox } from "./pages/SimpleInbox";
import { CompanySettings } from "./pages/CompanySettings";
import { DesignGuide } from "./pages/DesignGuide";
import { AgentDashboards } from "./pages/AgentDashboards";
import { AgentDashboardDetail } from "./pages/AgentDashboardDetail";
import { OrgChart } from "./pages/OrgChart";
import { AgentStudio } from "./pages/AgentStudio";
import { AgentBuilder } from "./pages/AgentBuilder";
import { NewAgent } from "./pages/NewAgent";
import { AgentArchitect } from "./pages/AgentArchitect";
import { WorkflowStudio } from "./pages/WorkflowStudio";
import { WorkflowBuilder } from "./pages/WorkflowBuilder";
import { AgentChat } from "./pages/AgentChat";
import { Documentation } from "./pages/Documentation";
import { TechWorkflow } from "./pages/TechWorkflow";
import { CustomTools } from "./pages/CustomTools";
import { AdminUsers } from "./pages/AdminUsers";
import { AdminRoles } from "./pages/AdminRoles";
import { AdminMetricProposals } from "./pages/AdminMetricProposals";
import { AdminLookerSchema } from "./pages/AdminLookerSchema";
import { AdminContexts } from "./pages/AdminContexts";
import { AdminBudget } from "./pages/AdminBudget";
import { AdminAlertChannels } from "./pages/AdminAlertChannels";
import { AnalyticsStudio } from "./pages/AnalyticsStudio";
import { Home } from "./pages/Home";
import { Ask } from "./pages/Ask";
import { SimpleDashboards } from "./pages/SimpleDashboards";
import { useViewMode } from "./hooks/useViewMode";

function DefaultRedirect() {
  const { mode } = useViewMode();
  return <Navigate to={mode === "simple" ? "home" : "dashboard"} replace />;
}
import { AuthPage } from "./pages/Auth";
import { BoardClaimPage } from "./pages/BoardClaim";
import { InviteLandingPage } from "./pages/InviteLanding";
import { queryKeys } from "./lib/queryKeys";
import { usePractice } from "./context/PracticeContext";
import { useDialog } from "./context/DialogContext";

function BootstrapPendingPage() {
  return (
    <div className="mx-auto max-w-xl py-10">
      <div className="rounded-lg border border-border bg-card p-6">
        <h1 className="text-xl font-semibold">Instance setup required</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          No instance admin exists yet. Run this command in your Ligament environment to generate
          the first admin invite URL:
        </p>
        <pre className="mt-4 overflow-x-auto rounded-md border border-border bg-muted/30 p-3 text-xs">
{`pnpm ligament auth bootstrap`}
        </pre>
      </div>
    </div>
  );
}

function CloudAccessGate() {
  const location = useLocation();
  const healthQuery = useQuery({
    queryKey: queryKeys.health,
    queryFn: () => healthApi.get(),
    retry: false,
  });

  const isAuthenticatedMode = healthQuery.data?.deploymentMode === "authenticated";
  const sessionQuery = useQuery({
    queryKey: queryKeys.auth.session,
    queryFn: () => authApi.getSession(),
    enabled: isAuthenticatedMode,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });

  if (healthQuery.isLoading || (isAuthenticatedMode && sessionQuery.isLoading)) {
    return <div className="mx-auto max-w-xl py-10 text-sm text-muted-foreground">Loading...</div>;
  }

  if (healthQuery.error) {
    return (
      <div className="mx-auto max-w-xl py-10 text-sm text-destructive">
        {healthQuery.error instanceof Error ? healthQuery.error.message : "Failed to load app state"}
      </div>
    );
  }

  if (isAuthenticatedMode && healthQuery.data?.bootstrapStatus === "bootstrap_pending") {
    return <BootstrapPendingPage />;
  }

  if (isAuthenticatedMode && !sessionQuery.data) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  return <Outlet />;
}

// Inbox is mode-aware: Simple mode shows the unified chronological feed,
// Pro mode keeps the classic sectioned inbox.
function InboxRoute() {
  const { mode } = useViewMode();
  return mode === "simple" ? <SimpleInbox /> : <Inbox />;
}

// Ask is mode-aware: Simple mode gets the stripped-down consumer wrapper,
// Pro mode gets the full Analytics Studio.
function AskRoute() {
  const { mode } = useViewMode();
  return mode === "simple" ? <Ask /> : <AnalyticsStudio />;
}

// Dashboards is mode-aware: Simple mode gets a read-only swipe carousel,
// Pro mode gets the full editable dashboards grid.
function DashboardsRoute() {
  const { mode } = useViewMode();
  return mode === "simple" ? <SimpleDashboards /> : <AgentDashboards />;
}

function boardRoutes() {
  return (
    <>
      <Route index element={<DefaultRedirect />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="companies" element={<Companies />} />
      <Route path="company/settings" element={<CompanySettings />} />
      <Route path="integrations" element={<CompanySettings />} />
      <Route path="org" element={<Navigate to="/studio" replace />} />
      <Route path="studio" element={<AgentStudio />} />
      <Route path="studio/new" element={<AgentBuilder />} />
      <Route path="studio/:graphId" element={<AgentBuilder />} />
      <Route path="studio/topology" element={<OrgChart />} />
      <Route path="agents" element={<Navigate to="/agents/all" replace />} />
      <Route path="agents/all" element={<Agents />} />
      <Route path="agents/active" element={<Agents />} />
      <Route path="agents/paused" element={<Agents />} />
      <Route path="agents/error" element={<Agents />} />
      <Route path="agents/new" element={<NewAgent />} />
      <Route path="agents/architect" element={<AgentArchitect />} />
      <Route path="agents/:agentId" element={<AgentDetail />} />
      <Route path="agents/:agentId/:tab" element={<AgentDetail />} />
      <Route path="agents/:agentId/runs/:runId" element={<AgentDetail />} />
      <Route path="projects" element={<Projects />} />
      <Route path="projects/:projectId" element={<ProjectDetail />} />
      <Route path="projects/:projectId/overview" element={<ProjectDetail />} />
      <Route path="projects/:projectId/issues" element={<ProjectDetail />} />
      <Route path="projects/:projectId/issues/:filter" element={<ProjectDetail />} />
      <Route path="issues" element={<Issues />} />
      <Route path="issues/all" element={<Navigate to="/issues" replace />} />
      <Route path="issues/active" element={<Navigate to="/issues" replace />} />
      <Route path="issues/backlog" element={<Navigate to="/issues" replace />} />
      <Route path="issues/done" element={<Navigate to="/issues" replace />} />
      <Route path="issues/recent" element={<Navigate to="/issues" replace />} />
      <Route path="issues/:issueId" element={<IssueDetail />} />
      <Route path="goals" element={<Goals />} />
      <Route path="goals/:goalId" element={<GoalDetail />} />
      <Route path="approvals" element={<Navigate to="/approvals/pending" replace />} />
      <Route path="approvals/pending" element={<Approvals />} />
      <Route path="approvals/all" element={<Approvals />} />
      <Route path="approvals/:approvalId" element={<ApprovalDetail />} />
      <Route path="chat" element={<AgentChat />} />
      <Route path="chat/:agentId" element={<AgentChat />} />
      <Route path="docs" element={<Documentation />} />
      <Route path="tech-workflow" element={<TechWorkflow />} />
      <Route path="workflows" element={<WorkflowStudio />} />
      <Route path="workflows/:workflowId" element={<WorkflowBuilder />} />
      <Route path="custom-tools" element={<CustomTools />} />
      <Route path="costs" element={<Costs />} />
      <Route path="activity" element={<Activity />} />
      <Route path="inbox" element={<Navigate to="/inbox/new" replace />} />
      <Route path="inbox/new" element={<InboxRoute />} />
      <Route path="inbox/all" element={<InboxRoute />} />
      <Route path="analytics" element={<DashboardsRoute />} />
      <Route path="analytics/:dashboardId" element={<AgentDashboardDetail />} />
      <Route path="admin/practices" element={<Companies />} />
      <Route path="admin/users" element={<AdminUsers />} />
      <Route path="admin/roles" element={<AdminRoles />} />
      <Route path="admin/metric-proposals" element={<AdminMetricProposals />} />
      <Route path="admin/contexts" element={<AdminContexts />} />
      <Route path="admin/budget" element={<AdminBudget />} />
      <Route path="admin/looker-schema" element={<AdminLookerSchema />} />
      <Route path="admin/alert-channels" element={<AdminAlertChannels />} />
      <Route path="explore" element={<AnalyticsStudio />} />
      <Route path="home" element={<Home />} />
      <Route path="ask" element={<AskRoute />} />
      <Route path="admin/activity-log" element={<Activity />} />
      <Route path="admin/ehr-integration" element={<CompanySettings />} />
      <Route path="admin/ai-config" element={<CompanySettings />} />
      <Route path="admin/schema-builder" element={<CompanySettings />} />
      <Route path="design-guide" element={<DesignGuide />} />
    </>
  );
}

function CompanyRootRedirect() {
  const { companies, selectedPractice, loading } = usePractice();
  const { onboardingOpen } = useDialog();
  const { mode } = useViewMode();

  if (loading) {
    return <div className="mx-auto max-w-xl py-10 text-sm text-muted-foreground">Loading...</div>;
  }

  // Keep the first-run onboarding mounted until it completes.
  if (onboardingOpen) {
    return <NoCompaniesStartPage autoOpen={false} />;
  }

  const targetCompany = selectedPractice ?? companies[0] ?? null;
  if (!targetCompany) {
    return <NoCompaniesStartPage />;
  }

  // Simple mode lands on the consumer Home; Pro mode lands on the classic Dashboard.
  return <Navigate to={mode === "simple" ? "/home" : "/dashboard"} replace />;
}

function LegacyPrefixRedirect() {
  const location = useLocation();
  // Strip the first path segment (the old company prefix)
  const rest = location.pathname.replace(/^\/[^/]+/, "") || "/dashboard";
  return <Navigate to={`${rest}${location.search}${location.hash}`} replace />;
}

function NoCompaniesStartPage({ autoOpen = true }: { autoOpen?: boolean }) {
  const { openOnboarding } = useDialog();
  const opened = useRef(false);

  useEffect(() => {
    if (!autoOpen) return;
    if (opened.current) return;
    opened.current = true;
    openOnboarding();
  }, [autoOpen, openOnboarding]);

  return (
    <div className="mx-auto max-w-xl py-10">
      <div className="rounded-lg border border-border bg-card p-6">
        <h1 className="text-xl font-semibold">Create your first practice</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Get started by creating a practice.
        </p>
        <div className="mt-4">
          <Button onClick={() => openOnboarding()}>New Practice</Button>
        </div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <>
      <Routes>
        <Route path="auth" element={<AuthPage />} />
        <Route path="board-claim/:token" element={<BoardClaimPage />} />
        <Route path="invite/:token" element={<InviteLandingPage />} />

        <Route element={<CloudAccessGate />}>
          <Route index element={<CompanyRootRedirect />} />
          <Route element={<Layout />}>
            {boardRoutes()}
          </Route>
          {/* Legacy prefixed URLs: /VIP/dashboard → /dashboard */}
          <Route path=":_prefix/*" element={<LegacyPrefixRedirect />} />
        </Route>
      </Routes>
      <OnboardingWizard />
    </>
  );
}
